/**
 * Badge types for frontend
 */

import { Timestamp } from 'firebase/firestore';

export interface BadgeDefinition {
  badgeId: string; // Primary key (lowercase_with_underscores)
  name: string; // Display name (max 50 chars)
  description: string; // Description (max 200 chars)
  icon_url: string; // Storage URL with timestamp
  icon_updated_at: number; // Timestamp for cache-busting (milliseconds)
  // Present and true when the badge has no icon by design, as opposed to one
  // whose icon has not been uploaded yet (#1975). Never false: the API omits
  // the field rather than emitting false, so `absent` means "not imageless".
  imageless?: boolean;
  // The one catalogued entitlement whose holders are granted this badge
  // automatically (#2061). Returned by the ADMIN badge endpoints only; the
  // public `/v1/badges` responses never carry it. Absent means unlinked: the
  // API never emits null.
  linked_entitlement?: string;
  // Present and true when the badge is automatic-only (#2268): it exists on a
  // user only while they hold `linked_entitlement`, and cannot be granted by
  // hand or by a redeem code. Admin endpoints only, and only on a linked
  // badge; never false.
  automatic_only?: true;
  created_at: Timestamp; // When the badge was created
  updated_at?: Timestamp; // When the badge was last updated
}

/** The `granted_by` prefix of a grant the linked-badge mechanism made. */
const ENTITLEMENT_GRANT_PREFIX = 'entitlement:';

/**
 * The entitlement an automatic grant was made for, or null for a manual one
 * (an admin uid, `REDEEM_CODE`, `SEED_STAR_BADGE`, ...).
 */
export function entitlementOfGrant(
  grantedBy: string | undefined
): string | null {
  if (!grantedBy?.startsWith(ENTITLEMENT_GRANT_PREFIX)) return null;
  const id = grantedBy.slice(ENTITLEMENT_GRANT_PREFIX.length);
  return id.length > 0 ? id : null;
}

export interface UserBadge {
  badgeId: string; // Reference to /badges/{badgeId}
  earned_at?: string | Timestamp; // ISO 8601 string from API or Timestamp from Firestore
  // Who granted it: an admin uid, a system marker (`REDEEM_CODE`,
  // `SEED_STAR_BADGE`), or `entitlement:<id>` for an automatic grant (#2061).
  granted_by: string;
  // Admin views only (#2061): present and true when an admin has hidden the
  // badge. Never false. A hidden badge is absent from every non-admin view.
  hidden?: true;
}

export interface BadgeListResponse {
  badges: BadgeDefinition[];
  total: number;
  hasMore: boolean;
}

export interface UserBadgeListResponse {
  badges: UserBadge[];
  total: number;
  hasMore: boolean;
}

export interface BadgeAuditEntry {
  id: string;
  action: 'granted' | 'revoked';
  badgeId: string;
  userId: string;
  adminId: string;
  reason?: string;
  timestamp: Timestamp;
}

export interface BadgeAuditResponse {
  entries: BadgeAuditEntry[];
  total: number;
  hasMore: boolean;
}
